import {
  Clock,
  PauseCircle,
  CloseCircle,
  Archive,
  Teacher,
  Heart,
  Tree,
  People,
  Profile2User,
  ShieldTick,
  EmojiHappy,
  Briefcase,
  Judge,
  Paintbucket,
  Building4,
  Global,
  SecurityUser,
  Chart,
  Buildings,
  HeartAdd,
  Health,
  Flag,
  Pet,
  Lovely,
  Category,
} from "iconsax-react";
import CheckIcon from "@/shared/assets/icons/CheckIcon";
import type { BadgeColor } from "@/shared/ui";
import type { StatusMapping } from "@/shared/components/StatusIndicator/mappings";
import type { TaskStatus, TaskPriority, TaskType, TagColor } from "@/features/tasks";
import type { ProjectStatus, ProjectCategory } from "../types";

interface StatusMapEntry {
  label: string;
  color: BadgeColor;
  icon?: React.ReactNode;
}

export const PROJECT_STATUS_MAP: Record<ProjectStatus, StatusMapEntry> = {
  planned: {
    label: "status.planned",
    color: "info",
    icon: <Clock size={14} color="currentColor" />,
  },
  active: {
    label: "status.active",
    color: "positive",
    icon: <CheckIcon />,
  },
  paused: {
    label: "status.paused",
    color: "notice",
    icon: <PauseCircle size={14} color="currentColor" variant="Bold" />,
  },
  closed: {
    label: "status.closed",
    color: "negative",
    icon: <CloseCircle size={14} color="currentColor" variant="Bold" />,
  },
  archived: {
    label: "status.archived",
    color: "gray",
    icon: <Archive size={14} color="currentColor" variant="Bold" />,
  },
} as const;

export const TASK_STATUS_MAP: Record<TaskStatus, StatusMapEntry> = {
  backlog: { label: "taskStatus.backlog", color: "gray" },
  todo: { label: "taskStatus.todo", color: "info" },
  in_progress: { label: "taskStatus.inProgress", color: "notice" },
  blocked: { label: "taskStatus.blocked", color: "negative" },
  done: { label: "taskStatus.done", color: "positive" },
} as const;

export const TASK_STATUS_INDICATOR_MAP: Record<TaskStatus, StatusMapping> = {
  backlog: {
    bg: "bg-grey-500/40",
    dot: "bg-grey-500",
    titleKey: "columns.backlog",
    namespace: "ProjectsPage",
  },
  todo: {
    bg: "bg-blue-500/40",
    dot: "bg-blue-500",
    titleKey: "columns.todo",
    namespace: "ProjectsPage",
  },
  in_progress: {
    bg: "bg-orange-500/40",
    dot: "bg-orange-500",
    titleKey: "columns.inProgress",
    namespace: "ProjectsPage",
  },
  blocked: {
    bg: "bg-red-500/40",
    dot: "bg-red-500",
    titleKey: "columns.blocked",
    namespace: "ProjectsPage",
  },
  done: {
    bg: "bg-green-500/40",
    dot: "bg-green-500",
    titleKey: "columns.done",
    namespace: "ProjectsPage",
  },
} as const;

export const PRIORITY_MAP: Record<TaskPriority, StatusMapEntry> = {
  low: { label: "priority.low", color: "gray" },
  normal: { label: "priority.normal", color: "info" },
  high: { label: "priority.high", color: "notice" },
  critical: { label: "priority.critical", color: "negative" },
} as const;

export const TASK_STATUSES: TaskStatus[] = ["backlog", "todo", "in_progress", "blocked", "done"];

export const TASK_STATUSES_WITHOUT_BACKLOG: TaskStatus[] = ["todo", "in_progress", "blocked", "done"];

export const TASK_TYPE_MAP: Record<TaskType, { label: string; color: BadgeColor }> = {
  kpi: { label: "taskType.kpi", color: "info" },
  first_level_task: { label: "taskType.firstLevelTask", color: "notice" },
  task: { label: "taskType.task", color: "gray" },
} as const;

export const TAG_COLORS: TagColor[] = [
  "gray",
  "info",
  "positive",
  "notice",
  "negative",
  "primary",
  "yellow",
  "indigo",
  "pink",
  "lime",
];

export const PROJECT_CATEGORIES: ProjectCategory[] = [
  "education_sports",
  "health_lifestyle",
  "ecology",
  "youth_policy",
  "family_gender",
  "vulnerable_groups",
  "orphan_support",
  "employment",
  "human_rights",
  "culture_art",
  "cultural_heritage",
  "social_harmony",
  "probation",
  "gov_monitoring",
  "ngo_development",
  "volunteering",
  "hardship_support",
  "patriotic_education",
  "animal_protection",
  "kazakh_dog_breeds",
  "other_social",
];

interface CategoryMapEntry {
  label: string;
  description: string;
  icon: React.ReactNode;
}

export const CATEGORY_MAP: Record<ProjectCategory, CategoryMapEntry> = {
  "": { label: "", description: "", icon: null },
  education_sports: { label: "category.education_sports.short", description: "category.education_sports.full", icon: <Teacher size={16} color="currentColor" /> },
  health_lifestyle: { label: "category.health_lifestyle.short", description: "category.health_lifestyle.full", icon: <Heart size={16} color="currentColor" /> },
  ecology: { label: "category.ecology.short", description: "category.ecology.full", icon: <Tree size={16} color="currentColor" /> },
  youth_policy: { label: "category.youth_policy.short", description: "category.youth_policy.full", icon: <People size={16} color="currentColor" /> },
  family_gender: { label: "category.family_gender.short", description: "category.family_gender.full", icon: <Profile2User size={16} color="currentColor" /> },
  vulnerable_groups: { label: "category.vulnerable_groups.short", description: "category.vulnerable_groups.full", icon: <ShieldTick size={16} color="currentColor" /> },
  orphan_support: { label: "category.orphan_support.short", description: "category.orphan_support.full", icon: <EmojiHappy size={16} color="currentColor" /> },
  employment: { label: "category.employment.short", description: "category.employment.full", icon: <Briefcase size={16} color="currentColor" /> },
  human_rights: { label: "category.human_rights.short", description: "category.human_rights.full", icon: <Judge size={16} color="currentColor" /> },
  culture_art: { label: "category.culture_art.short", description: "category.culture_art.full", icon: <Paintbucket size={16} color="currentColor" /> },
  cultural_heritage: { label: "category.cultural_heritage.short", description: "category.cultural_heritage.full", icon: <Building4 size={16} color="currentColor" /> },
  social_harmony: { label: "category.social_harmony.short", description: "category.social_harmony.full", icon: <Global size={16} color="currentColor" /> },
  probation: { label: "category.probation.short", description: "category.probation.full", icon: <SecurityUser size={16} color="currentColor" /> },
  gov_monitoring: { label: "category.gov_monitoring.short", description: "category.gov_monitoring.full", icon: <Chart size={16} color="currentColor" /> },
  ngo_development: { label: "category.ngo_development.short", description: "category.ngo_development.full", icon: <Buildings size={16} color="currentColor" /> },
  volunteering: { label: "category.volunteering.short", description: "category.volunteering.full", icon: <HeartAdd size={16} color="currentColor" /> },
  hardship_support: { label: "category.hardship_support.short", description: "category.hardship_support.full", icon: <Health size={16} color="currentColor" /> },
  patriotic_education: { label: "category.patriotic_education.short", description: "category.patriotic_education.full", icon: <Flag size={16} color="currentColor" /> },
  animal_protection: { label: "category.animal_protection.short", description: "category.animal_protection.full", icon: <Pet size={16} color="currentColor" /> },
  kazakh_dog_breeds: { label: "category.kazakh_dog_breeds.short", description: "category.kazakh_dog_breeds.full", icon: <Lovely size={16} color="currentColor" /> },
  other_social: { label: "category.other_social.short", description: "category.other_social.full", icon: <Category size={16} color="currentColor" /> },
} as const;
